import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2, FileSpreadsheet, FileText, Loader2 } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";

/**
 * SharePrepareDialog — Fase 4.5
 * Genera un resumen ejecutivo del reporte listo para copiar/pegar (email, WhatsApp).
 *
 * Props:
 *  - open, onOpenChange
 *  - reportKey (e.g. "sales")
 *  - reportName (visible label)
 *  - filters (dict) — snapshot de filtros aplicados en el reporte
 *  - stats (dict, opcional) — datos que alimentan las variables del template
 *  - availableFormats (["pdf","csv"])
 *  - onDownload({format}) — callback provided by ExportMenu / caller (dispara la descarga real)
 *  - branchId (opcional para el snapshot)
 */
export function SharePrepareDialog({
  open, onOpenChange, reportKey, reportName, filters = {}, stats = {},
  availableFormats = ["pdf", "csv"], onDownload, branchId = null,
}) {
  const { hasPermission } = useAuth();
  const canGenerate = hasPermission("report_exports:generate");
  const canDownload = hasPermission("report_exports:download");

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadingFmt, setDownloadingFmt] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api.post("/report-exports/preview", {
      report_key: reportKey,
      report_name: reportName,
      filters,
      stats,
    })
      .then(({ data }) => {
        if (!alive) return;
        setSubject(data.subject || "");
        setBody(data.body || "");
      })
      .catch((err) => {
        if (!alive) return;
        toast.error(err?.response?.data?.detail || "No se pudo generar el resumen");
        setSubject("");
        setBody("");
        // Auto-close on server-side validation error (e.g. rango >1 año)
        if (err?.response?.status === 422) {
          onOpenChange(false);
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reportKey]);

  async function copyToClipboard(text, label) {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado al portapapeles`);
    } catch (_e) {
      // Fallback for older browsers
      const ta = document.createElement("textarea");
      ta.value = text; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); toast.success(`${label} copiado`); }
      catch { toast.error("No se pudo copiar"); }
      ta.remove();
    }
  }

  async function copyAll() {
    const combined = `Asunto: ${subject}\n\n${body}`;
    await copyToClipboard(combined, "Asunto + resumen");
  }

  async function downloadAndLog(fmt) {
    if (!canDownload) {
      toast.error("Sin permiso para descargar");
      return;
    }
    setDownloadingFmt(fmt);
    try {
      // Delegate actual download to caller (they know the endpoint)
      await onDownload({ format: fmt });
      // Log the export snapshot server-side
      if (canGenerate) {
        try {
          await api.post("/report-exports", {
            report_key: reportKey,
            report_name: reportName,
            format: fmt,
            filters_snapshot: filters,
            subject_suggested: subject,
            share_summary_snapshot: body,
            row_count: stats?.row_count ?? stats?.count ?? null,
            branch_id: branchId,
          });
        } catch (_e) { /* silencioso: el download principal ya funcionó */ }
      }
    } finally {
      setDownloadingFmt(null);
    }
  }

  const filterChips = Object.entries(filters)
    .filter(([_, v]) => v !== null && v !== undefined && v !== "" && v !== "all")
    .slice(0, 10);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="share-prepare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" /> Preparar para compartir
          </DialogTitle>
          <DialogDescription>
            Copia el asunto y el resumen para pegar en tu correo o WhatsApp, y descarga el archivo aparte.
            Sin envío automático — todo compartir es manual.
          </DialogDescription>
        </DialogHeader>

        {filterChips.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="share-filter-chips">
            {filterChips.map(([k, v]) => (
              <Badge key={k} variant="outline" className="text-xs">
                <span className="text-muted-foreground mr-1">{k}:</span>
                <span className="mono">{String(v)}</span>
              </Badge>
            ))}
          </div>
        )}

        <div className="grid gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Asunto sugerido</Label>
            <div className="flex gap-2">
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={loading}
                data-testid="share-subject"
              />
              <Button variant="outline" onClick={() => copyToClipboard(subject, "Asunto")} data-testid="share-copy-subject" type="button">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Resumen sugerido</Label>
            <Textarea
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={loading}
              className="mono text-xs"
              data-testid="share-body"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => copyToClipboard(body, "Resumen")} disabled={loading} data-testid="share-copy-body">
              <Copy className="h-4 w-4 mr-2" /> Copiar resumen
            </Button>
            <Button variant="outline" onClick={copyAll} disabled={loading} data-testid="share-copy-all">
              <Copy className="h-4 w-4 mr-2" /> Copiar asunto + resumen
            </Button>
          </div>
        </div>

        {!canDownload && (
          <Alert data-testid="share-no-download-alert">
            <AlertDescription>Tu rol no tiene permiso para descargar el archivo. Solo puedes copiar el resumen.</AlertDescription>
          </Alert>
        )}

        <DialogFooter className="flex flex-wrap gap-2">
          {canDownload && availableFormats.includes("csv") && (
            <Button
              variant="outline"
              onClick={() => downloadAndLog("csv")}
              disabled={loading || !!downloadingFmt}
              data-testid="share-download-csv"
            >
              {downloadingFmt === "csv"
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <FileSpreadsheet className="h-4 w-4 mr-2 text-emerald-600" />}
              Descargar CSV
            </Button>
          )}
          {canDownload && availableFormats.includes("pdf") && (
            <Button
              onClick={() => downloadAndLog("pdf")}
              disabled={loading || !!downloadingFmt}
              className="btn-primary-tenant"
              data-testid="share-download-pdf"
            >
              {downloadingFmt === "pdf"
                ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                : <FileText className="h-4 w-4 mr-2" />}
              Descargar PDF
            </Button>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="share-close">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
